import { useMutation, useQueryClient } from '@tanstack/solid-query'
import { isHTTPError } from 'ky'
import { deriveRewardPresentation } from '~/lib/derive-reward-presentation'
import { preloadRewardImages } from '~/lib/preload-reward-images'
import { getRewardTreeAssets } from '~/lib/reward-tree-assets'
import type { DrawResult } from '~/lib/reward-types'
import { api, getCurrentUserId } from '~/services/api'
import { itemsQueryKey } from './items'
import { adjustProductPurchaseStats } from './product-purchase-stats'
import { PurchaseFailure, type PurchaseVariables } from './purchase'
import { unlockedTreesQueryKey } from './unlocked-trees'

type LotteryDrawResponse = {
  results: Array<{
    draw_results?: DrawResult[]
    purchase?: { id: number }
  }>
}

export const useLotteryDraw = () => {
  const queryClient = useQueryClient()
  return useMutation(() => ({
    mutationFn: async (vars: PurchaseVariables) => {
      const userId = await getCurrentUserId()
      try {
        const response = await api.service
          .post(`store/users/${userId}/lottery-draw`, {
            json: { product_id: vars.productId, quantity: vars.quantity ?? 1 },
          })
          .json<LotteryDrawResponse>()
        const draw = response.results[0]?.draw_results?.[0]
        if (draw) {
          const presentation = deriveRewardPresentation(draw)
          await preloadRewardImages(getRewardTreeAssets(presentation.treeGid))
        }
        return response
      } catch (error) {
        if (isHTTPError(error)) {
          if (error.response.status === 422) {
            throw new PurchaseFailure({ kind: 'insufficient-currency' })
          }
          if (error.response.status === 409) {
            throw new PurchaseFailure({ kind: 'daily-limit' })
          }
        }
        throw error
      }
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['user'] })
      // Bump the cached today_count synchronously (instead of invalidating)
      // so the buy button disables before the user can tap it again — an
      // invalidate-triggered refetch leaves a window where the stale
      // `remaining` keeps the button enabled and the next attempt 409s.
      adjustProductPurchaseStats(
        queryClient,
        vars.productId,
        vars.quantity ?? 1,
      )
      queryClient.invalidateQueries({ queryKey: unlockedTreesQueryKey })
      queryClient.invalidateQueries({ queryKey: itemsQueryKey })
    },
  }))
}
